import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import StarRatings from "react-star-ratings"
import LanguageDropdown from "components/CommonForBoth/TopbarDropdown/LanguageDropdown"
import { applyDocumentLanguage } from "utils/i18nDocument"
import {
  capturePublicRatingTenantId,
  fetchPublicRatingPage,
  submitPublicRating,
} from "helpers/publicRatingApi"
import PhoneNumberField from "components/Shared/PhoneNumberField"
import { validatePersonName } from "utils/personNameValidation"
import { DEFAULT_PHONE_COUNTRY, isValidPhoneValue } from "utils/phoneUtils"
import { buildRatingThemeStyle } from "utils/publicRatingTheme"
import {
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min"
import "./ratingPublic.scss"

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dob: "",
  feedback: "",
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normalizePhoneCountries = codes =>
  [
    ...new Set(
      (Array.isArray(codes) ? codes : [])
        .map(code => String(code || "").trim().toUpperCase())
        .filter(code => isSupportedCountry(code))
    ),
  ]

const pickDefaultPhoneCountry = codes => {
  if (codes.includes("SA")) {
    return "SA"
  }
  return codes[0] || DEFAULT_PHONE_COUNTRY
}

const splitPhoneForSubmit = (phone, allowedCountries = []) => {
  const parsed = parsePhoneNumberFromString(String(phone || "").trim())
  if (!parsed?.countryCallingCode || !parsed?.nationalNumber) {
    return null
  }

  if (allowedCountries.length) {
    const country = String(parsed.country || "").toUpperCase()
    const callingCode = String(parsed.countryCallingCode)
    const isAllowed =
      (country && allowedCountries.includes(country)) ||
      allowedCountries.some(code => {
        try {
          return String(getCountryCallingCode(code)) === callingCode
        } catch (_err) {
          return false
        }
      })
    if (!isAllowed) {
      return null
    }
  }

  return {
    phoneCode: `+${parsed.countryCallingCode}`,
    phoneNumber: String(parsed.nationalNumber),
  }
}

const AppPublicRating = () => {
  const { branchId } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.language === "sa" ? "sa" : "en"
  const isRtl = lang === "sa"
  const questionLang = lang === "sa" ? "ar" : "en"

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [pageError, setPageError] = useState("")
  const [formError, setFormError] = useState("")
  const [page, setPage] = useState(null)
  const [tenantId, setTenantId] = useState("")
  const [form, setForm] = useState(INITIAL_FORM)
  const [answers, setAnswers] = useState({})
  const [step, setStep] = useState(0)

  document.title = t("Rate your visit")

  useEffect(() => {
    const current = localStorage.getItem("I18N_LANGUAGE") || i18n.language || "en"
    if (i18n.language !== current) {
      i18n.changeLanguage(current)
    }
    applyDocumentLanguage(current)
  }, [i18n, i18n.language])

  useEffect(() => {
    let cancelled = false

    const loadPage = async () => {
      if (!branchId) {
        setPageError("This rating link is invalid or no longer available.")
        setLoading(false)
        return
      }

      const capturedTenantId = capturePublicRatingTenantId()
      if (!capturedTenantId) {
        setPageError("This rating link is invalid or no longer available.")
        setLoading(false)
        return
      }

      setTenantId(capturedTenantId)
      setLoading(true)
      setPageError("")
      setPage(null)
      try {
        const language = localStorage.getItem("I18N_LANGUAGE") || "en"
        const data = await fetchPublicRatingPage(
          branchId,
          language,
          capturedTenantId
        )
        if (cancelled) {
          return
        }
        setPage(data || null)
        setStep(0)
        setAnswers({})
        setForm(INITIAL_FORM)
        setSubmitted(false)
      } catch (_err) {
        if (!cancelled) {
          setPage(null)
          setPageError("This rating link is invalid or no longer available.")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPage()
    return () => {
      cancelled = true
    }
  }, [branchId])

  const questions = page?.questions || []
  const contactStep = questions.length
  const feedbackStep = questions.length + 1
  const isQuestionStep = step < questions.length
  const isContactStep = questions.length > 0 && step === contactStep
  const isFeedbackStep = questions.length > 0 && step === feedbackStep
  const currentQuestion = isQuestionStep ? questions[step] : null
  const progressPercent = questions.length
    ? Math.round(
        ((Math.min(step, questions.length - 1) + 1) / questions.length) * 100
      )
    : 0

  const branchName = useMemo(() => {
    if (!page?.branch) {
      return ""
    }
    return questionLang === "ar"
      ? page.branch.nameAr || page.branch.name
      : page.branch.name
  }, [page, questionLang])

  const phoneCountries = useMemo(
    () => normalizePhoneCountries(page?.tenant?.phoneCountries),
    [page]
  )
  const defaultPhoneCountry = useMemo(
    () => pickDefaultPhoneCountry(phoneCountries),
    [phoneCountries]
  )
  const showThemedBackground = Boolean(page?.theme) && !loading && !pageError
  const themeStyle = useMemo(
    () => (showThemedBackground ? buildRatingThemeStyle(page.theme) : undefined),
    [page, showThemedBackground]
  )

  const handleChange = event => {
    const { name, value } = event.target
    setForm(prev => ({ ...prev, [name]: value }))
    setFormError("")
  }

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
    setFormError("")
  }

  const validatePhone = () => {
    const phone = String(form.phone || "").trim()
    return Boolean(
      phone &&
        isValidPhoneValue(phone) &&
        splitPhoneForSubmit(phone, phoneCountries)
    )
  }

  const firstNameError = validatePersonName(form.firstName, { required: false })
  const lastNameError = validatePersonName(form.lastName, { required: false })

  const validateContact = () => {
    const requiredFirstNameError = validatePersonName(form.firstName, {
      required: true,
    })
    if (requiredFirstNameError) return requiredFirstNameError
    if (lastNameError) return lastNameError
    const email = String(form.email || "").trim()
    if (email && !EMAIL_PATTERN.test(email)) {
      return "Please enter a valid email address."
    }
    if (!validatePhone()) {
      return "Please enter a valid phone number."
    }
    return ""
  }

  const goPrevious = () => {
    setFormError("")
    setStep(prev => Math.max(0, prev - 1))
  }

  const goNext = () => {
    if (isQuestionStep) {
      if (!answers[currentQuestion?._id]) {
        setFormError("Please select a rating to continue")
        return
      }
      setFormError("")
      setStep(prev => prev + 1)
      return
    }

    if (isContactStep) {
      const contactError = validateContact()
      if (contactError) {
        setFormError(contactError)
        return
      }
      setFormError("")
      setStep(prev => prev + 1)
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setFormError("")

    if (!questions.length) {
      setFormError("Ratings are not available for this location yet.")
      return
    }

    if (!isFeedbackStep) {
      goNext()
      return
    }

    const missing = questions.some(question => !answers[question._id])
    if (missing) {
      setFormError("Please answer all questions.")
      return
    }

    const contactError = validateContact()
    if (contactError) {
      setFormError(contactError)
      return
    }

    const firstName = form.firstName.trim()
    const lastName = form.lastName.trim()
    const phoneParts = splitPhoneForSubmit(form.phone, phoneCountries)
    if (!phoneParts) {
      setFormError("Please enter a valid phone number.")
      return
    }

    setSubmitting(true)
    try {
      await submitPublicRating(
        {
          branchId,
          userInfo: {
            firstName,
            lastName,
            name: [firstName, lastName].filter(Boolean).join(" "),
            email: form.email.trim(),
            phoneCode: phoneParts.phoneCode,
            phoneNumber: phoneParts.phoneNumber,
            dob: form.dob.trim(),
          },
          ratings: questions.map(question => ({
            questionId: question._id,
            rating: Number(answers[question._id]),
          })),
          feedback: form.feedback.trim(),
        },
        lang,
        tenantId || capturePublicRatingTenantId() || page?.tenant?.id
      )
      setSubmitted(true)
    } catch (err) {
      setFormError(err.message || "Unable to submit rating")
    } finally {
      setSubmitting(false)
    }
  }

  const renderQuestionControl = question => {
    const max = Number(question.scale) || 5
    const current = Number(answers[question._id] || 0)
    const label =
      question.question?.[questionLang] ||
      question.question?.en ||
      question.question?.ar ||
      ""
    const isScale = Number(question.type) === 1

    return (
      <div className="rating-public__question" key={question._id}>
        <div className="rating-public__question-header">
          <p className="rating-public__question-kicker">
            {t("Question {{current}} / {{total}}", {
              current: step + 1,
              total: questions.length,
            })}
          </p>
          <p className="rating-public__question-title">{label}</p>
        </div>
        <div className="rating-public__controls">
          <p className="rating-public__score">
            <strong>{current}</strong>
            <span> / {max}</span>
          </p>
          {isScale ? (
            <div
              className="rating-public__scale-block"
              style={{ "--scale-max": max }}
            >
              <div className="rating-public__scale" role="group">
                {Array.from({ length: max }, (_, index) => index + 1).map(
                  value => (
                    <button
                      type="button"
                      key={`${question._id}-${value}`}
                      className={`rating-public__scale-btn${
                        current === value ? " is-active" : ""
                      }`}
                      onClick={() => handleAnswer(question._id, value)}
                    >
                      {value}
                    </button>
                  )
                )}
              </div>
              <div className="rating-public__scale-labels">
                <span>{t("Not likely")}</span>
                <span>{t("Extremely likely")}</span>
              </div>
            </div>
          ) : (
            <div className="rating-public__stars">
              <StarRatings
                rating={current}
                starRatedColor="#F1B44C"
                starEmptyColor="#cbd5e1"
                starHoverColor="#F1B44C"
                numberOfStars={max}
                name={`rating-${question._id}`}
                starDimension="32px"
                starSpacing="6px"
                changeRating={value => handleAnswer(question._id, value)}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderSurvey = () => (
    <form className="rating-public__form" onSubmit={handleSubmit}>
      <div className="rating-public__progress">
        {!isQuestionStep ? (
          <span>
            {isContactStep
              ? t("Your details")
              : t("Feedback")}
          </span>
        ) : null}
        <div
          className="rating-public__progress-track"
          aria-hidden="true"
        >
          <div
            className="rating-public__progress-fill"
            style={{
              width: `${
                isQuestionStep ? progressPercent : 100
              }%`,
            }}
          />
        </div>
      </div>

      {isQuestionStep ? renderQuestionControl(currentQuestion) : null}

      {isContactStep ? (
        <>
          <div className="rating-public__field-row">
            <div className="rating-public__field">
              <label htmlFor="rating-first-name">
                {t("First Name")}
                <span className="rating-public__required" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="rating-first-name"
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={handleChange}
                autoComplete="given-name"
              />
              {firstNameError ? (
                <p className="rating-public__error">{t(firstNameError)}</p>
              ) : null}
            </div>
            <div className="rating-public__field">
              <label htmlFor="rating-last-name">{t("Last Name")}</label>
              <input
                id="rating-last-name"
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={handleChange}
                autoComplete="family-name"
              />
              {lastNameError ? (
                <p className="rating-public__error">{t(lastNameError)}</p>
              ) : null}
            </div>
          </div>
          <div className="rating-public__field">
            <label htmlFor="rating-email">{t("Email")}</label>
            <input
              id="rating-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
          <div className="rating-public__field">
            <label htmlFor="rating-dob">{t("Date of Birth")}</label>
            <input
              id="rating-dob"
              name="dob"
              type="date"
              dir="ltr"
              value={form.dob}
              onChange={handleChange}
              autoComplete="bday"
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="rating-public__field">
            <label htmlFor="rating-phone">
              {t("Phone Number")}
              <span className="rating-public__required" aria-hidden="true">
                *
              </span>
            </label>
            <PhoneNumberField
              name="phone"
              id="rating-phone"
              value={form.phone}
              onChange={value => {
                setForm(prev => ({ ...prev, phone: value || "" }))
                setFormError("")
              }}
              countries={phoneCountries}
              defaultCountry={defaultPhoneCountry}
              placeholder={t("Phone Number")}
              hasError={formError === "Please enter a valid phone number."}
            />
          </div>
        </>
      ) : null}

      {isFeedbackStep ? (
        <div className="rating-public__field">
          <label htmlFor="rating-feedback">{t("Feedback")}</label>
          <textarea
            id="rating-feedback"
            name="feedback"
            value={form.feedback}
            onChange={handleChange}
            placeholder={t("Add any extra comments")}
          />
        </div>
      ) : null}

      {formError ? <p className="rating-public__error">{t(formError)}</p> : null}

      <div className="rating-public__nav">
        <button
          type="button"
          className="rating-public__back"
          onClick={goPrevious}
          disabled={step === 0}
        >
          {t("Previous")}
        </button>
        {isFeedbackStep ? (
          <button
            type="submit"
            className="rating-public__submit"
            disabled={submitting}
          >
            {submitting ? t("Submitting") : t("Submit rating")}
          </button>
        ) : (
          <button type="submit" className="rating-public__submit">
            {t("Next")}
          </button>
        )}
      </div>
    </form>
  )

  return (
    <div
      className={`rating-public${
        showThemedBackground ? "" : " rating-public--plain"
      }`}
      dir={isRtl ? "rtl" : "ltr"}
      style={themeStyle}
    >
      <div className="rating-public__bg" aria-hidden="true" />

      <header className="rating-public__header">
        <LanguageDropdown />
      </header>

      <main className="rating-public__main">
        {showThemedBackground ? (
          <section className="rating-public__hero">
            {page?.theme?.logoUrl ? (
              <img
                className="rating-public__logo"
                src={page.theme.logoUrl}
                alt={page?.tenant?.name || ""}
              />
            ) : null}
            <span className="rating-public__eyebrow">
              {page?.tenant?.name || t("Rating")}
            </span>
            {isContactStep ? (
              <p>{t("Share a quick rating for this location.")}</p>
            ) : isFeedbackStep ? (
              <p>{t("Add any extra comments")}</p>
            ) : null}
          </section>
        ) : null}

        <article className="rating-public__card rating-public__card--featured">
          {!loading && !pageError && !submitted && questions.length ? (
            <div className="rating-public__branch-heading">
              <p className="rating-public__branch-label">
                {t("You're rating this branch")}
              </p>
              <h1 className="rating-public__branch-title">
                {branchName || t("Rate your visit")}
              </h1>
            </div>
          ) : null}
          {loading ? (
            <div className="rating-public__status">
              <span className="rating-public__status-icon" aria-hidden="true">
                <i className="bx bx-loader-alt bx-spin" />
              </span>
              <h2>{t("Loading")}</h2>
            </div>
          ) : pageError ? (
            <div className="rating-public__status">
              <span className="rating-public__status-icon" aria-hidden="true">
                <i className="bx bx-error-circle" />
              </span>
              <h2>{t("Rating unavailable")}</h2>
              <p>{t(pageError)}</p>
            </div>
          ) : submitted ? (
            <div className="rating-public__status">
              <span
                className="rating-public__status-icon rating-public__status-icon--ok"
                aria-hidden="true"
              >
                <i className="bx bx-check" />
              </span>
              <h2>{t("Thank you")}</h2>
              <p>{t("Your rating has been submitted.")}</p>
            </div>
          ) : !questions.length ? (
            <div className="rating-public__status">
              <span className="rating-public__status-icon" aria-hidden="true">
                <i className="bx bx-star" />
              </span>
              <h2>{t("Rating unavailable")}</h2>
              <p>{t("Ratings are not available for this location yet.")}</p>
            </div>
          ) : (
            renderSurvey()
          )}
        </article>
      </main>
    </div>
  )
}

export default AppPublicRating
